

import React, { useState, useEffect, useRef } from "react";
import CardSelect from "../card-select";
import allFields from "./fields";
import SizingCounters from "../counter/SizingCounters";
import FileUploadField from "./fileUploadField";
import { getOrder, sendSupportRequest } from "../../services/contact-form";
import { PRODUCT_TYPES, REASON, isPatioProduct } from "./fields";
import Tooltip from "./tooltip";
import ScrollIntoView from "./scrollIntoView";
import ThankYou from "./thank-you";
import TicketExist from "./ticket-exist";

const ContactForm = ({ data }) => {
    const [contactForm, setContactForm] = useState({
        fullName: "",
        email: "",
        orderNumber: "",
    });

    const fieldRefs = useRef(new Map());
    const [selectedOrderItems, setSelectedOrderItems] = useState([]);
    const [order, setOrder] = useState(null);
    const [itemFormsData, setItemFormsData] = useState({});
    const [errors, setErrors] = useState({});
    const [orderError, setOrderErrors] = useState(null);
    const [areAllItemsComplete, setAreAllItemsComplete] = useState(false);
    const [tempSelectedOrderItems, setTempSelectedOrderItems] = useState([]);
    const [fieldValidationError, setFieldValidationError] = useState([]);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [loading, setLoading] = useState();
    const [showTicketExist, setShowTicketExist] = useState(false);
    const [fields, setFields] = useState([
        {
            id: 1,
            label: "Full Name",
            key: "fullName",
            type: "text",
            placeholder: "Enter your full name",
            required: true,
            validate: (value) => {
                return value ? null : "Please enter your full name";
            }
        },
        {
            id: 2,
            label: "Email",
            key: "email",
            type: "email",
            placeholder: "Enter your email",
            required: true,
            validate: (value) => {
                if (value && !isValidEmail(value)) {
                    return "Please enter a valid email";
                }
                return value ? null : "Please enter your email";
            }
        },
        {
            id: 3,
            label: "Order Number",
            key: "orderNumber",
            type: "text",
            placeholder: "Enter your order number",
            required: true,
            validate: (value) => {
                if (!value) {
                    return "Please enter your order number";
                }
                if (!/^\d+$/.test(value)) {
                    return "Please enter a valid order number";
                }
                return value ? null : "Please enter your order number";
            }
        }
    ]);

    const [showNextItemButton, setShowNextItemButton] = useState(false);


    useEffect(() => {

        //  VALIDATE IF ALL ITEMS HAVE BRIEF DESCRIPTION
        if (Object.keys(itemFormsData).length === 0) {
            return;
        }
        const allHaveBriefDescription = Object.values(itemFormsData)
            .every(item => 'briefDescription' in item);

        //  CHECK IF THE LAST ITEM HAS A BRIEF DESCRIPTION
        let lastIttemShown = tempSelectedOrderItems[tempSelectedOrderItems.length - 1]?.id;

        const lastItem = itemFormsData[lastIttemShown];


        if (lastItem?.briefDescription !== undefined && lastItem?.briefDescription !== null) {
            setShowNextItemButton(true);
        } else {
            setShowNextItemButton(false);
        }


        setAreAllItemsComplete(allHaveBriefDescription);


    }, [itemFormsData]);

    useEffect(() => {
        let lastItemToScroll = document.getElementsByClassName('ticket-page__item-form')[tempSelectedOrderItems.length - 1] || null;
        if (lastItemToScroll) {
            window.scrollTo(0, lastItemToScroll.offsetTop - 32);
        }
    }, [tempSelectedOrderItems]);

    function isValidEmail(email) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }


    const fetchOrder = async (orderNumber) => {
        try {
            const response = await getOrder(contactForm);
            setOrderErrors(response?.order?.error);
            return response;
        } catch (error) {
            setOrderErrors(error);
        }
        return null;
    }

    const handleContactForm = (e) => {
        setErrors(prev => ({ ...prev, [e.target.name]: null }));
        setContactForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSelectOrderItem = (item) => {
        setSelectedOrderItems((prev) => {
            const exists = prev.some(i => i.id === item.id);
            if (exists) {
                setItemFormsData(prevData => {
                    const newData = { ...prevData };
                    delete newData[item.id];
                    return newData;
                });
                return prev.filter(i => i.id !== item.id);
            } else {
                setItemFormsData(prevData => ({
                    ...prevData,
                    [item.id]: {}
                }));
                return [...prev, item];
            }
        });
    };

    const imageFieldStatus = (itemId, field) => {
        let itemData = Object.values(itemFormsData[itemId]) || [];
        let productType = getProductType(itemId);

        if (productType === PRODUCT_TYPES.CUSTOM && itemFormsData[itemId].reason === REASON.FITMENT_SIZE_ISSUES) {
            return 'required';
        }
        if (productType !== PRODUCT_TYPES.CUSTOM && itemFormsData[itemId].reason === REASON.FITMENT_SIZE_ISSUES) {
            return 'optional';
        }
        if (field.requiredImage.some(type => itemData.includes(type))) {
            return 'required';
        } else if (field.optionalImage.some(type => itemData.includes(type))) {
            return 'optional';
        } else if (field.hideImage.some(type => itemData.includes(type))) {
            return 'hidden';
        }
        return null;
    }

    const descriptionFieldStatus = (itemId, field) => {
        let itemData = Object.values(itemFormsData[itemId]) || [];
        if (field.requiredDescription.some(type => itemData.includes(type))) {
            return 'required';
        }
        return 'optional';
    }


    const handleOrderSubmit = async (e) => {

        setSelectedOrderItems([]);
        setItemFormsData({});
        setTempSelectedOrderItems([]);
        setShowNextItemButton(false);
        setAreAllItemsComplete(false);

        const errors = fields.filter(field => field.validate).map(field => {
            const error = field.validate(contactForm[field.key]);
            if (error) {
                return { [field.key]: error };
            }
            return null;
        }).filter(Boolean);

        setErrors(errors.reduce((acc, curr) => ({ ...acc, ...curr }), {}));
        if (Object.keys(errors).length > 0) {
            return;
        }
        e.preventDefault();
        setOrder(null);
        const order = await fetchOrder(contactForm.orderNumber);
        setOrder(order?.order);

    };


    const setRef = (itemId, element, fieldKey) => {
        if (element) {
            fieldRefs.current.set(`${itemId}-${fieldKey}`, element);
        } else {
            fieldRefs.current.delete(`${itemId}-${fieldKey}`);
        }
    };
    const handleNextItem = () => {
        setShowNextItemButton(false);
        setTempSelectedOrderItems([...tempSelectedOrderItems, selectedOrderItems[tempSelectedOrderItems.length]]);
    }

    const getProductType = (itemId) => {
        const productTitle = selectedOrderItems.find(item => item.id === itemId)?.title;

        const accessoriesAsCars = [
            'Seal Skin Organizer',
            'Seal Skin Back Seat Tablet Organizer',
            'Seal Skin Back Seat Organizer',
            'Trunk organizer - Black',
            'Trunk organizer - Blue',
            'Seal Skin Trunk Organizer - Black',
            'Seal Skin Trunk Organizer - Blue',
            'Seal Skin Boat Outboard Motor Cover',
            'Outboard Motor Covers',
            'Outboard Motor Cover 60HP GREY',
            'Microfiber Extendable Cleaning Duster',
            'Microfiber Cloth (3 pack)',
            'Seal Skin Boat Cover Support Pole',
            'Seal Skin Boat Cover Support Pole Deluxe',
            'Truck Cover Support Pole Deluxe',
            'Support pole',
            '2 Support Poles',
            'Cover Storage Bag',
            'Cable Lock',
        ]

        if (productTitle?.toLowerCase().includes('car') ||
            productTitle?.toLowerCase().includes('suv') ||
            productTitle?.toLowerCase().includes('van') ||
            productTitle?.toLowerCase().includes('truck') ||
            accessoriesAsCars.includes(productTitle)) {
            return PRODUCT_TYPES.CARS;
        }

        if (productTitle?.toLowerCase().includes('motorcycle')) {
            return PRODUCT_TYPES.MOTORCYCLES;
        }

        if (productTitle?.toLowerCase().includes('atv') ||
            productTitle?.toLowerCase().includes('utv') ||
            productTitle?.toLowerCase().includes('snowmobile') ||
            productTitle?.toLowerCase().includes('scooter')) {
            return PRODUCT_TYPES.MOTORSPORTS;
        }

        if (productTitle?.toLowerCase().includes('jet ski')) {
            return PRODUCT_TYPES.JETSKIS;
        }

        if (productTitle?.toLowerCase().includes('boat') ||
            productTitle?.toLowerCase().includes('pontoon') ||
            productTitle?.toLowerCase().includes('bimini')) {
            return PRODUCT_TYPES.BOATS;
        }

        return PRODUCT_TYPES.CUSTOM;
    }

    const handleItemFieldChange = (itemId, fieldName, value) => {
        // setFieldValidationError(prev => prev.filter(error => error !== `${itemId}-${fieldName}`));
        const fieldMapping = {
            'briefDescription': 'brief-description',
            'image_ids': 'image-upload',
        }
        setFieldValidationError(prev => prev.filter(error => error !== `${itemId}-${fieldMapping[fieldName]}`));
        setItemFormsData(prev => {
            const currentItemData = prev[itemId] || {};
            const newItemData = { ...currentItemData, [fieldName]: value };

            const updatedItemData = clearDependentFields(newItemData, fieldName);



            return {
                ...prev,
                [itemId]: updatedItemData
            };
        });
    };

    const clearDependentFields = (data, changedField) => {
        const newData = { ...data };

        const dependencies = {
            reason: ['otherIssue', 'wrongCoverType', 'missingItemType', 'warrantyIssue', 'qualityIssue', 'fitmentIssue', 'fitmentSizeInstructions', 'sizingCounters'],
            otherIssue: ['wrongCoverType', 'missingItemType'],
            fitmentIssue: ['fitmentSizeInstructions', 'sizingCounters'],
            fitmentSizeInstructions: ['sizingCounters'],
            sizingCounters: [],
            wrongCoverType: [],
            missingItemType: [],
            warrantyIssue: [],
            qualityIssue: [],
            customInstructions: [],
            cancelOrder: [],
        };

        if (dependencies[changedField]) {
            dependencies[changedField].forEach(dependentField => {
                delete newData[dependentField];
            });
        }

        return newData;
    };

    const getVisibleFields = (itemId) => {
        const itemData = itemFormsData[itemId] || {};

        const productType = getProductType(itemId);

        return allFields.filter(field => {
            if (!field.if) return true;
            return field.if(itemData, productType);
        });
    };

    const renderField = (field, itemId, item) => {

        const Component = field.component;
        const itemData = itemFormsData[itemId] || {};

        const helper = typeof field.helper === 'function' ? field.helper(getProductType(itemId), itemData, item) : field.helper

        if (Component === CardSelect) {

            const filteredOptions = field.options?.filter(option => {
                if (!option.productTypes) {
                    return true;
                }
                return option.productTypes.includes(getProductType(itemId));
            }) || [];

            const cardSelectOptions = filteredOptions?.map((option, index) => {
                const optionLabel = isPatioProduct(item) && option.patioLabel
                    ? option.patioLabel
                    : option.label;

                return {
                    id: option.value,
                    label: optionLabel,
                    title: optionLabel,
                    value: option.value,
                    icon: option?.icon
                };
            }) || [];

            return (
                <div key={`${itemId}-${field.name}`} className="ticket-page__field">
                    <div className="ticket-page__field-label">
                        {helper ? (
                            <label className="ticket-page__field-helper" htmlFor={field.key}>{helper}</label>
                        ) : (
                            <label htmlFor={field.key}>{field.label}</label>
                        )}
                    </div>

                    <ScrollIntoView />
                    <CardSelect
                        options={cardSelectOptions}
                        onSelect={(selectedOption) => handleItemFieldChange(itemId, field.name, selectedOption.value)}
                        selected={cardSelectOptions.find(opt => opt.value === itemData[field.name]) || null}
                        multiple={false}
                        size="medium"
                        viewOnly={false}
                        cols={1}
                    />

                    <hr />
                </div>
            );
        }
        if (Component === SizingCounters) {
            return (
                <div key={`${itemId}-${field.name}`} className="ticket-page__field ticket-page__field--counter">
                    <ScrollIntoView />
                    <SizingCounters
                        label={field.label}
                        productType={getProductType(itemId)}
                        value={itemData[field.name] || { width: 0, height: 0, length: 0 }}
                        onChange={(value) => handleItemFieldChange(itemId, field.name, value)}
                        helper={helper}
                        units="in"
                    />

                </div>
            );
        }
        if (Component === "briefDescription") {

            if (!itemData.hasOwnProperty('briefDescription')) {
                handleItemFieldChange(itemId, 'briefDescription', '');
            }

            return (
                <div>

                    {!(itemData.reason === REASON.FITMENT_SIZE_ISSUES && getProductType(itemId) === PRODUCT_TYPES.CUSTOM) && (
                        <ScrollIntoView />
                    )}
                    {helper && (
                        <div className="ticket-page__field-helper ticket-page__field-helper--brief-description">
                            {helper}
                        </div>
                    )}
                    <div className={`ticket-page__field-brief-description ${imageFieldStatus(itemId, field) === 'hidden' ? 'ticket-page__field-brief-description--hidden' : ''}`}>

                        <div
                            ref={(el) => setRef(itemId, el, 'brief-description')}
                            key={`${itemId}-brief-description`}
                            className="ticket-page__field"
                        >
                            <div className={`ticket-page__field-label ${descriptionFieldStatus(itemId, field) === 'required' ? 'ticket-page__field--required' : ''}`}>
                                <Tooltip show={fieldValidationError.includes(`${itemId}-brief-description`)} text="Please provide a brief description">
                                    <label>{field.label}</label>
                                </Tooltip>
                            </div>
                            <textarea
                                label={field.label}
                                value={itemData.briefDescription || ''}
                                onChange={(e) => handleItemFieldChange(itemId, field.name, e.target.value)}
                                placeholder={field.placeholder}
                            />
                        </div>

                        {(imageFieldStatus(itemId, field) === 'required' || imageFieldStatus(itemId, field) === 'optional') && (
                            <div
                                ref={(el) => setRef(itemId, el, 'image-upload')}
                                key={`${itemId}-image-upload`}
                                className={`ticket-page__field ${imageFieldStatus(itemId, field) === 'required' ? 'ticket-page__field--required' : ''}`}
                            >
                                <Tooltip
                                    show={fieldValidationError.includes(`${itemId}-image-upload`)}
                                    text={(getProductType(itemId) === PRODUCT_TYPES.CUSTOM && itemFormsData[itemId].reason === REASON.FITMENT_SIZE_ISSUES) ? 'Upload an image for each step' : 'Please upload an image'}
                                >
                                    <div className="ticket-page__field-label">
                                        <label>Upload image</label>
                                    </div>
                                </Tooltip>

                                <p className="ticket-page__image-upload-helper">Please add a photo showing how the cover sits on the vehicle to help us confirm the issue and provide the right solution.</p>

                                <FileUploadField
                                    onFilesChange={(files) => handleItemFieldChange(itemId, 'image_ids', files.map(f => f))}
                                />
                            </div>
                        )}
                    </div>
                    <br />
                </div>


            );
        }

        const commonProps = {
            label: field.label,
            value: itemData[field.name] || '',
            onChange: (value) => handleItemFieldChange(itemId, field.name, value),
            helper: helper,
            ...field
        };

        return (
            <div key={`${itemId}-${field.name}`} className="ticket-page__field">
                <Component {...commonProps} />
            </div>
        );
    };

    const validateRequiredFields = () => {
        const validationErrors = [];

        tempSelectedOrderItems.forEach(item => {
            const itemId = item.id;
            const itemData = itemFormsData[itemId] || {};
            const visibleFields = getVisibleFields(itemId);
            const productType = getProductType(itemId);
            const requiredImages = (productType === PRODUCT_TYPES.CUSTOM && itemData.reason === REASON.FITMENT_SIZE_ISSUES) ? 3 : 0;

            const briefDescriptionField = visibleFields.find(field => field.component === "briefDescription");

            if (briefDescriptionField) {
                const imageStatus = imageFieldStatus(itemId, briefDescriptionField);
                const descriptionStatus = descriptionFieldStatus(itemId, briefDescriptionField);
                if (descriptionStatus === 'required' && (!itemData.briefDescription || itemData.briefDescription.trim().length === 0)) {

                    validationErrors.push({
                        itemId: itemId,
                        fieldKey: `${itemId}-brief-description`,
                        error: 'Please provide a brief description'
                    });
                }


                if (imageStatus === 'required' && (!itemData.image_ids || itemData.image_ids.length < requiredImages)) {
                    validationErrors.push({
                        itemId: itemId,
                        fieldKey: `${itemId}-image-upload`,
                        error: 'Image upload is required for this item'
                    });
                }
            }
        });

        return validationErrors;
    };
    const handleFinalSubmit = async () => {


        const fieldValidationError = validateRequiredFields();

        if (fieldValidationError.length > 0) {
            const firstErrorFieldKey = fieldValidationError[0].fieldKey;
            const firstErrorElement = fieldRefs.current.get(firstErrorFieldKey);

            setFieldValidationError((prev) => [...prev, firstErrorFieldKey]);

            if (firstErrorElement) {
                window.scrollTo({
                    top: firstErrorElement.offsetTop - 100,
                    behavior: 'smooth'
                });
            }

            return;
        } else {
            setFieldValidationError([]);
        }


        try {
            setLoading(prev => ({ ...prev, finalSubmit: true }));
            await sendSupportRequest({ ...contactForm, items: itemFormsData });

            setIsSubmitted(true);
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        } catch (error) {
            if (error.response.data.ticket_exists) {
                setShowTicketExist(true);
                return;
            } else {
                setErrors(prev => ({ ...prev, 'ticketError': 'Sorry, we couldn’t submit your request. Please try again.' }));
                console.error('Error sending support request:', error);
            }
        } finally {
            setLoading(prev => ({ ...prev, finalSubmit: false }));
        }

    };

    const handleOrderItemSelection = () => {
        setTempSelectedOrderItems([selectedOrderItems[0]]);
    }

    if (showTicketExist) {
        return <TicketExist />;
    }


    return (
        <div className="ticket-page__wrapper">

            {!isSubmitted && (
                <>
                    <div className="ticket-page__header">
                        <h1>We’re Here to Help</h1>
                        <p>Tell us what you need, and our team will take care of the rest.</p>
                    </div>

                    {/* <pre>
                        {JSON.stringify(itemFormsData, null, 2)}
                    </pre> */}

                    <div className="ticket-page">
                        <div className="ticket-page__card">
                            <div className="ticket-page__fields ticket-page__contact">
                                {fields.map((field) => (
                                    <div className={`ticket-page__field ${field.required ? 'ticket-page__field--required' : ''}`} key={field.id}>
                                        <Tooltip show={errors[field.key]} text={errors[field.key]}>
                                            <label htmlFor={field.key}>{field.label}</label>
                                        </Tooltip>

                                        <input
                                            type={field.type}
                                            id={field.key}
                                            name={field.key}
                                            value={contactForm[field.key]}
                                            onChange={handleContactForm}
                                            placeholder={field.placeholder}
                                        />
                                        {/* {errors[field.key] && <small className="ticket-page__field-helper">{errors[field.key]}</small>} */}
                                    </div>
                                ))}
                            </div>
                            <div className="ticket-page__footer ticket-page__footer--contact">
                                <button className="ticket-page__button" onClick={handleOrderSubmit}>
                                    Open a ticket
                                </button>
                            </div>


                            {orderError && (
                                <div className="ticket-page__error">
                                    <p>{orderError}</p>
                                </div>
                            )}

                            {order && !orderError && (
                                <>
                                    <ScrollIntoView block="start" />
                                    <hr />
                                    <div className="ticket-page__order">
                                        <p className="ticket-page__order-title">
                                            Select the Products you need help with
                                            <span> (you can select more than one product)</span>
                                        </p>
                                        <CardSelect
                                            options={order?.lineItems}
                                            onSelect={handleSelectOrderItem}
                                            multiple={true}
                                            selected={selectedOrderItems}
                                            cols={3}
                                            className="ticket-page__order-card"
                                        >
                                            {(option) => {
                                                return (
                                                    <p>
                                                        {option?.customAttributes?.FITS}
                                                    </p>
                                                )
                                            }}
                                        </CardSelect>
                                        <div className={`ticket-page__order-footer ${selectedOrderItems.length === 0 ? 'ticket-page__order-footer--disabled' : ''}`}>
                                            <p>{selectedOrderItems.length} product(s) selected</p>
                                            <button
                                                className="ticket-page__button ticket-page__button--primary"
                                                onClick={handleOrderItemSelection}
                                                disabled={selectedOrderItems.length === 0}
                                            >
                                                Confirm and continue
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {tempSelectedOrderItems.map((i, index) => {
                            const item = { ...i, productType: getProductType(i.id) };
                            const visibleFields = getVisibleFields(item.id);

                            return (
                                <div key={`${item.id}-${index}`} className="ticket-page__item-form ticket-page__card">
                                    {tempSelectedOrderItems.length > 1 && (
                                        <p className="order-item__index">
                                            Product {index + 1} of {selectedOrderItems.length}
                                        </p>
                                    )}
                                    <CardSelect
                                        size="small"
                                        options={[item]}
                                        onSelect={() => { }}
                                        selected={item}
                                        viewOnly={true}
                                        fluid={true}
                                    >
                                        {item.productType !== PRODUCT_TYPES.CUSTOM && (
                                            <p>{item?.customAttributes?.FITS}</p>
                                        )}
                                    </CardSelect>

                                    <div className="ticket-page__dynamic-fields">
                                        {visibleFields.map(field => renderField(field, item.id, item))}
                                    </div>
                                    {showNextItemButton && tempSelectedOrderItems.length !== selectedOrderItems.length && (
                                        <div className="ticket-page__footer">
                                            <button
                                                className="ticket-page__button ticket-page__button--primary"
                                                onClick={handleNextItem}
                                            >
                                                Confirm and continue
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}


                        {areAllItemsComplete && (
                            <div style={{ marginTop: "48px" }} className="ticket-page__footer--contact">
                                <button
                                    className={`ticket-page__button ticket-page__button--primary ${loading?.finalSubmit ? 'ticket-page__button--loading' : ''}`}
                                    onClick={handleFinalSubmit}
                                >
                                    {loading?.finalSubmit ? 'Submitting...' : 'Submit Support Request'}
                                </button>
                            </div>
                        )}

                        {errors.ticketError && (
                            <div className="ticket-page__error">
                                <p>{errors.ticketError}</p>
                            </div>
                        )}
                    </div>
                </>
            )}
            {isSubmitted && (
                <ThankYou />
            )}
        </div>
    );
};

export default ContactForm;